import axios, { type AxiosError } from 'axios';
import type { ApiResponse } from '@pmc/shared';

/**
 * Admin API client.
 *
 * Unlike the citizen portal (which rides on a session cookie), the admin app
 * authenticates with a bearer token. The token is held in sessionStorage rather
 * than localStorage so it dies with the browser tab — an admin workstation is
 * more likely to be shared than a citizen's phone.
 */

const TOKEN_KEY = 'pmc.admin.token';

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

const rawBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '/citizen/api/';
const baseURL = rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`;

const client = axios.create({
  baseURL,
  timeout: 30_000,
});

export class ApiFailureError extends Error {
  readonly code: string;
  readonly fields: Record<string, string>;
  readonly status: number;

  constructor(code: string, message: string, fields: Record<string, string>, status: number) {
    super(message);
    this.name = 'ApiFailureError';
    this.code = code;
    this.fields = fields;
    this.status = status;
  }
}

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** An expired or revoked token drops the admin back to the sign-in screen. */
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      setToken(null);
      if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

async function request<T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  body?: unknown,
  params?: Record<string, unknown>,
): Promise<T> {
  try {
    const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
    const response = await client.request<any>({
      method,
      url: cleanUrl,
      ...(body !== undefined ? { data: body } : {}),
      ...(params ? { params } : {}),
    });
    const payload = response.data;
    if (payload && (payload.ok === true || payload.success === true)) {
      return (payload.data !== undefined ? payload.data : payload) as T;
    }
    if (payload && payload.ok === undefined && payload.success === undefined) {
      return payload as T;
    }
    const errorObj = payload?.error || {};
    const code = errorObj.code || (payload?.success === false ? 'API_ERROR' : 'UNKNOWN_ERROR');
    const message = errorObj.message || payload?.message || 'An error occurred';
    const fields = errorObj.fields || {};
    throw new ApiFailureError(code, message, fields, response.status);
  } catch (err) {
    if (err instanceof ApiFailureError) throw err;

    const axiosError = err as AxiosError<any>;
    const payload = axiosError.response?.data;

    if (payload && typeof payload === 'object' && (payload.ok === false || payload.success === false)) {
      const errorObj = payload.error || {};
      throw new ApiFailureError(
        errorObj.code || 'API_ERROR',
        errorObj.message || payload.message || 'Request failed',
        errorObj.fields ?? {},
        axiosError.response?.status ?? 0,
      );
    }
    throw new ApiFailureError(
      'NETWORK_ERROR',
      'Could not reach the server. Please try again.',
      {},
      axiosError.response?.status ?? 0,
    );
  }
}

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    request<T>('get', url, undefined, params),
  post: <T>(url: string, body?: unknown) => request<T>('post', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('put', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('patch', url, body),
  delete: <T>(url: string) => request<T>('delete', url),

  /**
   * Document URLs need the bearer token, which an <img> tag cannot send —
   * so the file is fetched and turned into an object URL instead.
   *
   * Server-generated document URLs already carry a leading `/api`, but
   * `client`'s own `baseURL` supplies that same prefix — passed through
   * unchanged the two stack into `/api/api/...` and 404. Strip it and let
   * axios prepend `baseURL` the same way it does for every other call.
   */
  fetchBlobUrl: async (path: string): Promise<string> => {
    const relativePath = path.startsWith('/api') ? path.slice(4) : path;
    const response = await client.get(relativePath, { responseType: 'blob' });
    return URL.createObjectURL(response.data as Blob);
  },
};
